import React from 'react';
import Button from '../../components/Button/Button';
import ListViewFilterChips from './ListViewFilterChips/ListViewFilterChips';
import ListViewFilterPanel from './ListViewFilterPanel';
import useListViewFilterEditor from '../../hooks/useListViewFilterEditor';
import type { ActiveFilter, FilterTreeItem } from '../types';

/* Filter builder for the list view: an "Add filter" button opening a panel that
 * drills down through the filter tree (system fields and the custom data fields
 * of the fetched bookings) to a field, where the filter values are picked. The
 * active filters are shown as removable chips; clicking a chip's pencil reopens
 * the panel on that field with the current values, to edit the filter. */

interface ListViewFiltersProps {
  filterTree: FilterTreeItem[];
  activeFilters: ActiveFilter[];
  onAddFilter: (filter: ActiveFilter) => void;
  onUpdateFilter: (filter: ActiveFilter) => void;
  onRemoveFilter: (filterID: string) => void;
}

const ListViewFilters = ({
  filterTree,
  activeFilters,
  onAddFilter,
  onUpdateFilter,
  onRemoveFilter
}: ListViewFiltersProps) => {
  const editor = useListViewFilterEditor(filterTree, onAddFilter, onUpdateFilter);
  return (
    <div tw="flex items-center min-w-0 w-full">
      <div tw="relative flex-none mr-8">
        <Button
          size="small"
          color="default"
          variant="outlined"
          onClick={editor.toggle}
          dataAttributes={{ 'data-test-list-view-add-filter': true }}
        >
          {i18n('Add filter')}
        </Button>
        {editor.isOpen && <ListViewFilterPanel editor={editor} />}
      </div>
      <ListViewFilterChips
        activeFilters={activeFilters}
        onEditFilter={editor.editFilter}
        onRemoveFilter={onRemoveFilter}
      />
    </div>
  );
};

export default ListViewFilters;
